import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingMedium,
    Title,
} from "@khanacademy/wonder-blocks-typography";
import * as Cookies from "@repo/data-cookies";
import {RequestInfo} from "@repo/shared-core";

import PageRoot from "#app-shell-package/page-root.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLink from "#components/link-package/deprecated-link.ts";
import CookiesProvider from "#data-access/cookies-provider.tsx";

import FeedbackMessage from "./feedback-message.tsx";
import {withStrings} from "./locales/check-access/load-strings.tsx";
import VideoPlayerQuiz from "./video-player-quiz.tsx";

type Props = WithActionSchedulerProps;

type State = {
    videoQuizStep: number;
    recommendations: Array<number>;
    thirdPartyCookies: CanAccessStates;
    iframeCompatible: CanAccessStates;
};

export type CanAccessStates = "LOADING" | "TRUE" | "FALSE" | "UNKNOWN";

class CheckAccess extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            videoQuizStep: 0,
            recommendations: [],
            thirdPartyCookies: "LOADING",
            iframeCompatible: "LOADING",
        };
    }

    componentDidMount() {
        window.addEventListener("message", this._receiveMessage, false);
        this.props.schedule.timeout(() => {
            if (this.state.iframeCompatible === "LOADING") {
                this.setState({iframeCompatible: "FALSE"});
            }
            if (this.state.thirdPartyCookies === "LOADING") {
                this.setState({thirdPartyCookies: "UNKNOWN"});
            }
        }, 5000); // In the video player if we can't play a video in an iframe within 5s we assume there is an issue and fallback to the backup player
    }

    _receiveMessage: (event: MessageEvent) => void = (event: MessageEvent) => {
        if (
            typeof event.data !== "string" ||
            !event.data.startsWith("KA:3PC")
        ) {
            return;
        }

        this.setState({iframeCompatible: "TRUE"});

        if (event.data === "KA:3PCunsupported") {
            this.setState({thirdPartyCookies: "FALSE"});
        } else if (event.data === "KA:3PCsupported") {
            this.setState({thirdPartyCookies: "TRUE"});
        }
    };

    _areFirstPartyCookiesEnabled: () => boolean = () => {
        const checkWithTestCookie = () => {
            Cookies.createCookie(
                "ka-testcookie",
                Cookies.CookieCategory.Functional,
            );
            return document.cookie.indexOf("ka-testcookie") !== -1;
        };
        return navigator.cookieEnabled || checkWithTestCookie();
    };

    _renderBrowser: () => React.ReactNode = () => (
        <View>
            <HeadingMedium style={styles.heading}>
                {t`Browser`}
                <Strut size={spacing.medium_16} />
                <Body>
                    <DeprecatedLink
                        highlighted={true}
                        href="https://support.khanacademy.org/hc/articles/204795660-Which-browsers-are-supported-"
                    >
                        {t`Learn more`}
                    </DeprecatedLink>
                </Body>
            </HeadingMedium>
            <Body>
                {RequestInfo.isUnsupportedBrowser ? (
                    <FeedbackMessage
                        message={t`We do not support your browser. We support the latest versions of Firefox, Chrome, Safari, and Edge`}
                        success={false}
                    />
                ) : (
                    <FeedbackMessage
                        message={t`We support the latest version of your browser`}
                        success={true}
                    />
                )}
            </Body>
        </View>
    );

    _renderCookies: () => React.ReactNode = () => (
        <View>
            <HeadingMedium style={styles.heading}>
                {t`Site Cookies`}
                <Strut size={spacing.medium_16} />
                <Body>
                    <DeprecatedLink
                        highlighted={true}
                        href="https://support.khanacademy.org/hc/articles/360021112132-How-do-I-allow-cookies-from-Khan-Academy-"
                    >
                        {t`Learn more`}
                    </DeprecatedLink>
                </Body>
            </HeadingMedium>
            <Body>
                {this._areFirstPartyCookiesEnabled() ? (
                    <FeedbackMessage
                        message={t`First party cookie check passed`}
                        success={true}
                    />
                ) : (
                    <FeedbackMessage
                        message={t`First party cookie check failed`}
                        success={false}
                    />
                )}
                {this.state.thirdPartyCookies === "LOADING" && (
                    <View>
                        <FeedbackMessage
                            loading={true}
                            message={t`Third party cookie check is pending`}
                        />
                    </View>
                )}
                {this.state.thirdPartyCookies === "TRUE" && (
                    <FeedbackMessage
                        message={t`Third party cookie check passed`}
                        success={true}
                    />
                )}
                {this.state.thirdPartyCookies === "FALSE" && (
                    <FeedbackMessage
                        message={t`Third party cookie check failed`}
                        success={false}
                    />
                )}
                {this.state.thirdPartyCookies === "UNKNOWN" && (
                    <FeedbackMessage
                        message={t`Third party cookie check could not be completed because the iframe check failed`}
                        success={false}
                    />
                )}
                {/* This hidden iframe is used for two purposes: to test if we
                can render something from an iframe, and to check if
                third-party cookies are enabled. However, the latter entails
                setting a test cookie, something that we can only do if we have
                the user's consent. It's much easier for us to check for
                consent here than it is to duplicate the logic for parsing
                consent in the barebones code loaded in the iframe, so we pass
                the iframe a querystring param to indicate if cookies can't be
                set. */}
                <View style={styles.hiddenIframe}>
                    <CookiesProvider
                        mockOnFirstRender={false}
                        placeholder={null}
                    >
                        {(Cookies) => {
                            const consentData =
                                Cookies.getConsentCookieCategories();
                            const canSetTestCookie =
                                consentData.get(
                                    Cookies.CookieCategory.Functional,
                                ) ?? false;

                            // Without specifying kastatic.org directly, the following would be
                            // proxied through khanacademy.org, which in prod defeats the purpose
                            // of the check (setting a cookie from a different domain)
                            const baseUrl =
                                // eslint-disable-next-line static-service/dont-hardcode-cdn
                                "https://cdn.kastatic.org/check-access-third-party-cookie-test.html";

                            return (
                                // eslint-disable-next-line jsx-a11y/iframe-has-title -- TODO: Address a11y error
                                <iframe
                                    style={styles.hiddenIframe}
                                    id="TPCIframe"
                                    src={`${baseUrl}?cookiesDisallowed=${!canSetTestCookie}`}
                                    height={0}
                                    width={0}
                                />
                            );
                        }}
                    </CookiesProvider>
                </View>
            </Body>
        </View>
    );

    _renderTestIframes: () => React.ReactNode = () => (
        <View>
            <HeadingMedium style={styles.heading}>{t`Iframes`}</HeadingMedium>
            <Body>
                {this.state.iframeCompatible === "LOADING" && (
                    <FeedbackMessage
                        message={t`Iframe check pending`}
                        loading={true}
                    />
                )}
                {this.state.iframeCompatible === "TRUE" && (
                    <FeedbackMessage
                        message={t`Iframe check passed`}
                        success={true}
                    />
                )}
                {this.state.iframeCompatible === "FALSE" && (
                    <FeedbackMessage
                        message={t`Iframe check failed`}
                        success={false}
                    />
                )}
            </Body>
        </View>
    );

    render(): React.ReactNode {
        return (
            <PageRoot>
                <View style={styles.container}>
                    <Title style={styles.heading}>
                        {t`Khan Academy Access Check`}
                    </Title>
                    <Body>
                        {t`This page detects some of the common network issues described in this article: `}
                        <DeprecatedLink
                            highlighted={true}
                            href="https://support.khanacademy.org/hc/articles/203580580-Which-sites-domains-do-I-whitelist-so-my-students-can-access-all-of-Khan-Academy-"
                        >
                            {t`Which sites/domains do I safelist so my students can access all of Khan Academy?`}
                        </DeprecatedLink>
                    </Body>
                    {this._renderBrowser()}
                    {this._renderCookies()}
                    {this._renderTestIframes()}
                    <VideoPlayerQuiz />
                </View>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        maxWidth: 800,
        margin: "20px auto 60px",
    },
    heading: {
        margin: "1em 0",
        flexDirection: "row",
        alignItems: "baseline",
    },
    hiddenIframe: {
        position: "absolute",
        height: 0,
        width: 0,
    },
});

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof CheckAccess,
        React.ComponentProps<typeof CheckAccess>
    >
>;

export default withStrings(
    withActionScheduler(CheckAccess) as React.ComponentType<ExportProps>,
);
